import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Header from "../components/Header";
import Link from "../components/Link";
import PageTitle from "../components/PageTitle";
import ModalSucesso from "../components/ModalSucesso";
import ModalCriarUsuario from "../components/ModalCriarUsuario";

import PermToggle from "./components/PermToggle";
import PermArrayChips from "./components/PermArrayChips";
import PermBooleanMap from "./components/PermBooleanMap";
import { PERMS_UI } from "./components/permsUiSchema";

import styles from "./styles/Usuarios.module.css";

const API = "https://provin.ind.br/api";

function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

function get(obj, path) {
  return path.reduce((acc, k) => (acc ? acc[k] : undefined), obj);
}

function set(obj, path, value) {
  const copy = deepClone(obj);
  let cur = copy;
  for (let i = 0; i < path.length - 1; i++) {
    cur[path[i]] = cur[path[i]] ?? {};
    cur = cur[path[i]];
  }
  cur[path[path.length - 1]] = value;
  return copy;
}

function getFromSection(sectionObj, it) {
  if (!sectionObj) return undefined;
  if (it.path?.length) return it.path.reduce((acc, k) => acc?.[k], sectionObj);
  return sectionObj?.[it.key];
}

function setInSection(perms, section, it, value) {
  const next = deepClone(perms);
  next[section] = next[section] ?? {};

  if (!it.path?.length) {
    next[section][it.key] = value;
    return next;
  }

  let cur = next[section];
  for (let i = 0; i < it.path.length - 1; i++) {
    cur[it.path[i]] = cur[it.path[i]] ?? {};
    cur = cur[it.path[i]];
  }
  cur[it.path[it.path.length - 1]] = value;

  return next;
}

export default function Usuarios() {
  const token = localStorage.getItem("token");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");

  const [isModalCriarUsuarioOpen, setModalCriarUsuarioOpen] = useState(false);

  const [usuarios, setUsuarios] = useState([]);
  const [usuarioSelecionadoId, setUsuarioSelecionadoId] = useState("");

  const usuarioSelecionado = useMemo(
    () =>
      usuarios.find((u) => String(u.id) === String(usuarioSelecionadoId)) ??
      null,
    [usuarios, usuarioSelecionadoId]
  );

  const [permissoes, setPermissoes] = useState(null);
  const [permissoesOriginais, setPermissoesOriginais] = useState(null);

  const [loadingPerms, setLoadingPerms] = useState(false);
  const [saving, setSaving] = useState(false);

  const dirty = useMemo(() => {
    if (!permissoes || !permissoesOriginais) return false;
    return JSON.stringify(permissoes) !== JSON.stringify(permissoesOriginais);
  }, [permissoes, permissoesOriginais]);

  // Listar usuários
  useEffect(() => {
    async function fetchUsuarios() {
      const response = await axios.get(`${API}/usuarios`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsuarios(response.data);
    }
    fetchUsuarios();
  }, [token]);

  // Buscar permissões do usuário selecionado
  useEffect(() => {
    async function fetchPermissoes() {
      if (!usuarioSelecionado) return;

      setLoadingPerms(true);
      try {
        const response = await axios.get(
          `${API}/usuarios/${usuarioSelecionado.id}/permissoes`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setPermissoes(response.data);
        setPermissoesOriginais(deepClone(response.data));
      } finally {
        setLoadingPerms(false);
      }
    }

    fetchPermissoes();
  }, [token, usuarioSelecionado]);

  const onBoolean = (path, checked) => {
    setPermissoes((prev) => set(prev, path, !!checked));
  };

  const onArray = (section, it, list) => {
    setPermissoes((prev) =>
      setInSection(prev, section, it, Array.isArray(list) ? list : [])
    );
  };

  const onBooleanMap = (section, it, nextMap) => {
    setPermissoes((prev) => setInSection(prev, section, it, nextMap || {}));
  };

  const salvarPermissoes = async () => {
    if (!usuarioSelecionado || !permissoes) return;

    setSaving(true);
    try {
      const resp = await axios.put(
        `${API}/usuarios/${usuarioSelecionado.id}/permissoes`,
        permissoes,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setPermissoes(resp.data.permissoes);
      setPermissoesOriginais(deepClone(resp.data.permissoes));

      setModalMessage("Permissões atualizadas com sucesso!");
      setIsModalOpen(true);
    } catch (e) {
      setModalMessage("Erro ao salvar permissões.");
      setIsModalOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const desfazerAlteracoes = () => {
    if (permissoesOriginais) setPermissoes(deepClone(permissoesOriginais));
  };

  const handleCreateUser = async (data) => {
    try {
      const response = await fetch(`${API}/api/usuarios/criarUsuario/novo`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setModalMessage("Usuário criado com sucesso!");
        setIsModalOpen(true);

        const list = await axios.get(`${API}/usuarios`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUsuarios(list.data);
      } else {
        setModalMessage("Erro ao criar usuário.");
        setIsModalOpen(true);
      }
    } catch {
      setModalMessage("Erro ao criar usuário.");
      setIsModalOpen(true);
    }
  };

  const paginasEstoquesAtiva = !!permissoes?.paginas?.estoques;

  return (
    <div className={styles.body}>
      <Header />
      <Link redirectTo="/home" />
      <PageTitle src="images/common/usuarios.svg" title="Usuários" />

      <div className={styles.container}>
        <div className={styles.topbar}>
          <div className={styles.left}>
            <button
              className={styles.btnPrimary}
              onClick={() => setModalCriarUsuarioOpen(true)}
            >
              + Novo
            </button>

            <div className={styles.selectBox}>
              <select
                value={usuarioSelecionadoId}
                onChange={(e) => setUsuarioSelecionadoId(e.target.value)}
              >
                <option value="">Selecione um usuário...</option>
                {usuarios.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.usuario}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className={styles.right}>
            <button
              className={styles.btnGhost}
              disabled={!dirty || saving || !usuarioSelecionado}
              onClick={desfazerAlteracoes}
              title="Voltar ao estado original"
            >
              Desfazer
            </button>

            <button
              className={styles.btnSave}
              disabled={!dirty || saving || !usuarioSelecionado}
              onClick={salvarPermissoes}
            >
              {saving ? "Salvando..." : "Salvar alterações"}
            </button>
          </div>
        </div>

        <ModalCriarUsuario
          isOpen={isModalCriarUsuarioOpen}
          onClose={() => setModalCriarUsuarioOpen(false)}
          onSave={handleCreateUser}
        />

        {!usuarioSelecionado ? (
          <div className={styles.emptyState}>
            Selecione um usuário para editar permissões.
          </div>
        ) : loadingPerms || !permissoes ? (
          <div className={styles.loading}>Carregando permissões...</div>
        ) : (
          <div className={styles.grid}>
            {PERMS_UI.map((block) => {
              // Admin (path global)
              if (block.type === "boolean" && block.path) {
                return (
                  <div className={styles.card} key={block.title}>
                    <div className={styles.cardHeader}>
                      <h2>{block.title}</h2>
                      <span className={styles.badge}>global</span>
                    </div>
                    <PermToggle
                      label="Administrador"
                      description={block.description}
                      checked={get(permissoes, block.path)}
                      onChange={(v) => onBoolean(block.path, v)}
                    />
                  </div>
                );
              }

              // Condição opcional: desabilitar bloco Estoques se pagina estoques = false
              const isEstoquesBlock =
                String(block.section || "").toLowerCase() === "estoques";
              const disabledBlock = isEstoquesBlock && !paginasEstoquesAtiva;

              return (
                <div
                  className={styles.card}
                  key={block.title}
                  style={disabledBlock ? { opacity: 0.6 } : undefined}
                >
                  <div className={styles.cardHeader}>
                    <h2>{block.title}</h2>
                    {disabledBlock ? (
                      <span className={styles.badge}>habilite “Estoques”</span>
                    ) : null}
                  </div>

                  {/* itens simples (booleans em section) */}
                  {block.items?.length ? (
                    <div className={styles.cardBody}>
                      {block.items.map((it) => (
                        <PermToggle
                          key={it.key}
                          label={it.label}
                          checked={permissoes[block.section]?.[it.key]}
                          onChange={(v) => {
                            if (disabledBlock) return;
                            setPermissoes((prev) => ({
                              ...prev,
                              [block.section]: {
                                ...prev[block.section],
                                [it.key]: v,
                              },
                            }));
                          }}
                        />
                      ))}
                    </div>
                  ) : null}

                  {/* grupos */}
                  {block.groups?.length ? (
                    <div className={styles.cardBody}>
                      {block.groups.map((g) => (
                        <div className={styles.group} key={g.subtitle}>
                          <div className={styles.groupTitle}>{g.subtitle}</div>

                          <div className={styles.groupGrid}>
                            {g.items.map((it) => {
                              const sectionObj = permissoes?.[block.section];
                              const val = getFromSection(sectionObj, it);

                              // Arrays (chips)
                              if (it.type === "array") {
                                return (
                                  <PermArrayChips
                                    key={it.key || it.path?.join(".")}
                                    label={it.label}
                                    value={val}
                                    onChange={(list) => {
                                      if (disabledBlock) return;
                                      onArray(block.section, it, list);
                                    }}
                                  />
                                );
                              }

                              // Boolean map (ex.: estoques.relatorio.filtros)
                              if (it.type === "booleanMap") {
                                return (
                                  <PermBooleanMap
                                    key={it.key || it.path?.join(".")}
                                    label={it.label}
                                    value={val}
                                    labels={it.labels || {}}
                                    onChange={(nextMap) => {
                                      if (disabledBlock) return;
                                      onBooleanMap(block.section, it, nextMap);
                                    }}
                                  />
                                );
                              }

                              // Boolean simples
                              return (
                                <PermToggle
                                  key={it.key}
                                  label={it.label}
                                  checked={!!val}
                                  onChange={(v) => {
                                    if (disabledBlock) return;
                                    setPermissoes((prev) =>
                                      setInSection(prev, block.section, it, !!v)
                                    );
                                  }}
                                />
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ModalSucesso
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        message={modalMessage}
      />
    </div>
  );
}